import { useId } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper/types'
import { A11y, Navigation } from 'swiper/modules'

import { Props_SnippetGalleryCarousel } from './GalleryCarousel.props'
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import SwiperControls, { swiperNavigationOptions } from '../Shared/SwiperControls/SwiperControls'
import SnippetLink from '../Shared/Link/Link'

const SnippetGalleryCarousel = (props: Props_SnippetGalleryCarousel) => {
    const { heading, content, link, repeatable_galleryItems } = props

    const reactId = useId()
    const uniqueId = reactId.replace(/:/g, '')

    const classHeadingVisibility: string = !heading.visibility.value ? 'snippet-gallery-carousel__heading--hidden' : ''

    const hasLink = Boolean(link.linkText?.value && link.linkHref?.value)

    const canLoop = (swiper: SwiperType) => {
        const currentSlidesPerView = Math.floor(swiper.params.slidesPerView as number)
        const shouldLoop = swiper.slides.length > currentSlidesPerView
        swiper.params.loop = shouldLoop
        if (Number.parseInt(swiper.currentBreakpoint) >= 1200 && swiper.slides.length > currentSlidesPerView + 3) {
            swiper.params.centeredSlides = true
        } else if (Number.parseInt(swiper.currentBreakpoint) >= 1200 && swiper.slides.length <= currentSlidesPerView + 3) {
            swiper.params.centeredSlides = false
            swiper.params.loop = false
        }
        swiper.loopDestroy()
        if (shouldLoop) {
            swiper.update()
            swiper.loopCreate()
        }
    }

    const swiperProps = {
        className: 'snippet-gallery-carousel__swiper',
        modules: [Navigation, A11y],
        slidesPerView: 1.1,
        spaceBetween: 25,
        grabCursor: true,
        watchOverflow: true,
        watchSlidesProgress: true,
        centeredSlides: false,
        loop: false,
        breakpoints: {
            420: {
                slidesPerView: 1.25,
            },
            768: {
                slidesPerView: 2,
            },
            991: {
                slidesPerView: 3,
            },
            1200: {
                slidesPerView: 4,
            },
        },
        navigation: {
            ...swiperNavigationOptions,
            prevEl: `.swiper-controls__button--prev-${uniqueId}`,
            nextEl: `.swiper-controls__button--next-${uniqueId}`,
        },
        onInit: canLoop,
        onResize: canLoop
    }

    const swiperControlsProps = {
        uniqueId,
        showNavigationMobile: true,
        showNavigationDesktop: true,
        showPaginationMobile: false,
        showPaginationDesktop: false,
        navigationPosition: 'below' as const,
        className: 'snippet-gallery-carousel__navigation',
    }

    return (
        <section className="snippet-gallery-carousel">
            <div className="snippet-gallery-carousel__container">
                <div className="snippet-gallery-carousel__container-content">
                    <h2 className={`snippet-gallery-carousel__heading ${classHeadingVisibility}`}>
                        {heading.content.value}
                    </h2>
                    {content.content?.value && (
                        <div className="snippet-gallery-carousel__content">
                            {CompileMarkdown(content.content.value)}
                        </div>
                    )}
                </div>

                <div className="snippet-gallery-carousel__container-carousel">
                    <Swiper {...swiperProps}>
                        {repeatable_galleryItems.map((item, index) => {
                            const { image, alt, heading: itemHeading, content, linkText, linkHref, linkStyle, linkTarget, linkAriaLabel } = item
                            if (!image?.value || !itemHeading?.value) return null

                            const hasItemLink = Boolean(linkText?.value && linkHref?.value)

                            return (
                                <SwiperSlide key={`snippet-gallery-carousel-${uniqueId}-${index}`} className="snippet-gallery-carousel__slide">
                                    <article className="snippet-gallery-carousel__card">
                                        <img
                                            className="snippet-gallery-carousel__card-image"
                                            src={image.value}
                                            alt={alt?.value || ''}
                                            loading="lazy"
                                        />
                                        <div className="snippet-gallery-carousel__card-overlay">
                                            <h3 className="snippet-gallery-carousel__card-heading">
                                                {itemHeading.value}
                                            </h3>
                                            {content?.value && (
                                                <div className="snippet-gallery-carousel__card-content">
                                                    {CompileMarkdown(content.value)}
                                                </div>
                                            )}
                                            {hasItemLink && (
                                                <SnippetLink
                                                    url={linkHref!.value}
                                                    linkStyles={linkStyle?.value}
                                                    openInNewTab={linkTarget?.value}
                                                    className="snippet-gallery-carousel__card-link"
                                                    aria-label={linkAriaLabel?.value || linkText?.value}
                                                >
                                                    {linkText!.value}
                                                </SnippetLink>
                                            )}
                                        </div>
                                    </article>
                                </SwiperSlide>
                            )
                        })}
                    </Swiper>
                </div>

                <SwiperControls {...swiperControlsProps} />

                {hasLink && (
                    <div className="snippet-gallery-carousel__container-link">
                        <SnippetLink
                            url={link.linkHref.value}
                            linkStyles={link.linkStyle?.value}
                            openInNewTab={link.linkTarget?.value}
                            className="snippet-gallery-carousel__link"
                            aria-label={link.linkAriaLabel?.value || link.linkText.value}
                        >
                            {link.linkText.value}
                        </SnippetLink>
                    </div>
                )}
            </div>
        </section>
    )
}

export default SnippetGalleryCarousel
